import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Calendar, ExternalLink, Info } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { SaveBar } from '@/components/ui/save-bar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useBrief, useUpdateBriefSlice, useSaveAndRebuildBrief } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief/booking')({
  staticData: { crumb: 'Rezerwacje' },
  component: BookingProviderPage,
});

/**
 * `brief.booking.provider` enum mirrors the brief schema (packages/brief/vet.ts).
 * 'in_house' is the default chosen by onboarding: it renders the native h2m
 * picker tied to /api/booking/<slug>. The external providers (booksy /
 * znanylekarz / calendly) render an iframe or anchor mount-point. 'none'
 * suppresses the booking section entirely.
 */
const PROVIDERS = [
  { value: 'in_house', label: 'h2m (rezerwacja na własnej stronie)' },
  { value: 'znanylekarz_weterynaria', label: 'ZnanyLekarz / ZnanyLekarz Weterynaria' },
  { value: 'booksy', label: 'Booksy' },
  { value: 'cal', label: 'Cal.com' },
  { value: 'calendly', label: 'Calendly' },
  { value: 'none', label: 'Brak — ukryj sekcję rezerwacji' },
] as const;

type ProviderValue = (typeof PROVIDERS)[number]['value'];

interface StoredBooking {
  provider?: ProviderValue;
  url?: string;
  booksy?: { business_id?: number; profile_url?: string };
}

function BookingProviderPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const saveAndRebuild = useSaveAndRebuildBrief();

  const stored = (brief?.booking ?? {}) as StoredBooking;
  const [provider, setProvider] = React.useState<ProviderValue>(stored.provider ?? 'in_house');
  const [url, setUrl] = React.useState<string>(stored.url ?? '');
  const [booksyBusinessId, setBooksyBusinessId] = React.useState<string>(
    stored.booksy?.business_id ? String(stored.booksy.business_id) : ''
  );
  const [booksyProfileUrl, setBooksyProfileUrl] = React.useState<string>(
    stored.booksy?.profile_url ?? ''
  );
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  React.useEffect(() => {
    if (!dirty) {
      setProvider(stored.provider ?? 'in_house');
      setUrl(stored.url ?? '');
      setBooksyBusinessId(stored.booksy?.business_id ? String(stored.booksy.business_id) : '');
      setBooksyProfileUrl(stored.booksy?.profile_url ?? '');
    }
  }, [stored.provider, stored.url, stored.booksy?.business_id, stored.booksy?.profile_url, dirty]);

  const buildPatch = () => {
    const booking: StoredBooking = { provider };
    if (provider !== 'in_house' && provider !== 'none' && url) booking.url = url;
    if (provider === 'booksy' && (booksyBusinessId || booksyProfileUrl)) {
      booking.booksy = {
        ...(booksyBusinessId ? { business_id: Number(booksyBusinessId) } : {}),
        ...(booksyProfileUrl ? { profile_url: booksyProfileUrl } : {}),
      };
    }
    return { booking };
  };

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      await update.mutateAsync({ patch: buildPatch() });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const onSaveAndRebuild = async () => {
    setSavedStatus('idle');
    try {
      await saveAndRebuild.mutateAsync({ patch: buildPatch() });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          System rezerwacji
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Wybierz, jak pacjenci umawiają wizyty na Twojej stronie. Domyślnie
          używamy własnego systemu — kalendarz hostujemy my, prowizji nie
          pobieramy, terminy pojawiają się w zakładce „Kalendarz wizyt".
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Dostawca rezerwacji</CardTitle>
          <CardDescription>
            Zmiana powoduje wymianę sekcji „Umów wizytę" na renderowanej
            stronie. Wymagane jest ponowne zbudowanie podglądu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Field label="Dostawca" htmlFor="booking-provider">
            <Select
              id="booking-provider"
              value={provider}
              onChange={(e) => {
                setProvider(e.currentTarget.value as ProviderValue);
                setDirty(true);
              }}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>

          {provider === 'in_house' ? (
            <div className="flex gap-3 rounded-md bg-success/10 p-4 text-sm text-success ring-1 ring-success/20">
              <Calendar className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-medium">Rezerwacja u nas — bez prowizji.</p>
                <p className="text-xs">
                  Pacjenci umawiają się przez Twoją stronę. Ustawienia
                  godzin pracy lekarzy i rezerwacje przychodzące znajdziesz
                  w zakładce „Kalendarz wizyt".
                </p>
              </div>
            </div>
          ) : null}

          {provider === 'none' ? (
            <div className="flex gap-3 rounded-md bg-warning/10 p-4 text-sm text-warning ring-1 ring-warning/20">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <p className="text-xs">
                Sekcja „Umów wizytę" zostanie ukryta. Pacjenci będą mogli
                zadzwonić lub skorzystać z formularza kontaktowego.
              </p>
            </div>
          ) : null}

          {provider === 'booksy' ? (
            <>
              <Field label="ID profilu Booksy" htmlFor="booksy-id" hint="liczba widoczna w URL profilu">
                <Input
                  id="booksy-id"
                  inputMode="numeric"
                  value={booksyBusinessId}
                  onChange={(e) => {
                    setBooksyBusinessId(e.currentTarget.value);
                    setDirty(true);
                  }}
                  placeholder="np. 123456"
                />
              </Field>
              <Field label="Pełny URL profilu Booksy" htmlFor="booksy-url">
                <Input
                  id="booksy-url"
                  type="url"
                  value={booksyProfileUrl}
                  onChange={(e) => {
                    setBooksyProfileUrl(e.currentTarget.value);
                    setDirty(true);
                  }}
                  placeholder="https://booksy.com/pl-pl/123456,nazwa-kliniki"
                />
              </Field>
            </>
          ) : null}

          {provider === 'znanylekarz_weterynaria' ||
          provider === 'cal' ||
          provider === 'calendly' ? (
            <Field label="URL profilu / kalendarza" htmlFor="booking-url">
              <Input
                id="booking-url"
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.currentTarget.value);
                  setDirty(true);
                }}
                placeholder="https://…"
              />
            </Field>
          ) : null}

          {provider !== 'in_house' && provider !== 'none' ? (
            <p className="flex items-center gap-1.5 text-xs text-text-subtle">
              <ExternalLink className="size-3.5" aria-hidden="true" />
              Po zapisie wyrenderujemy odpowiedni widżet zewnętrzny. Wewnętrzny
              kalendarz pozostaje wyłączony — wizyty nie pojawią się w
              zakładce „Kalendarz wizyt".
            </p>
          ) : null}
        </CardContent>
      </Card>

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        rebuilding={saveAndRebuild.isPending}
        onSaveAndRebuild={onSaveAndRebuild}
        onCancel={() => {
          setProvider(stored.provider ?? 'in_house');
          setUrl(stored.url ?? '');
          setBooksyBusinessId(stored.booksy?.business_id ? String(stored.booksy.business_id) : '');
          setBooksyProfileUrl(stored.booksy?.profile_url ?? '');
          setDirty(false);
        }}
      />
    </form>
  );
}
